import React from "react";
import { createRoot } from "react-dom/client";
import {
  Building2,
  ChevronRight,
  Bell,
  FileText,
  Home,
  Music,
  Music2,
  Search,
  SlidersHorizontal,
  Sparkles,
  TimerReset,
  Wrench
} from "lucide-react";
import "./styles.css";

const { lazy, Suspense } = React;

const currentYear = new Date().getFullYear();

const sections = [
  {
    slug: "general",
    title: "General",
    description: "Everyday utilities for planning, formatting, timing, and quick decisions.",
    icon: Wrench,
    tools: [
      {
        name: "Text Cleaner",
        summary: "Normalize pasted notes, titles, lists, and rough copy.",
        status: "Planned",
        icon: Sparkles
      },
      {
        name: "Quick Compare",
        summary: "A compact way to score options side by side.",
        status: "Planned",
        icon: SlidersHorizontal
      }
    ]
  },
  {
    slug: "music",
    title: "Music",
    description: "Free browser-based music tools for teachers, students, performers, directors, arrangers, and handbell musicians.",
    icon: Music,
    tools: [
      {
        name: "Staff Paper Generator",
        summary: "Create printable manuscript paper with custom page sizes, staff counts, spacing, margins, and PDF export.",
        status: "Open",
        href: "/tools/music/staff-paper-generator",
        icon: FileText,
        featured: true
      },
      {
        name: "Bell Usage Chart Maker",
        summary: "Upload MusicXML and generate a printable chart of every bell pitch used in the score.",
        status: "Open",
        href: "/tools/music/bell-usage-chart-maker",
        icon: Bell
      },
      {
        name: "Scale Generator",
        summary: "Generate printable major, minor, modal, and chromatic scales across multiple staff layouts.",
        status: "Open",
        href: "/tools/music/scale-generator",
        icon: Music
      },
      {
        name: "Instrument Range & Transposition Chart | Free Music Tool",
        summary: "Compare written range, concert range, clef, and sounding transposition for common instruments.",
        status: "Open",
        href: "/tools/music/instrument-range-transposition",
        icon: Search
      },
      {
        name: "Metronome",
        summary: "Set a BPM or tap one in, then practice with a steady browser-based click track.",
        status: "Open",
        href: "/tools/music/metronome",
        icon: Music2
      },
      {
        name: "Practice Log",
        summary: "Track focused reps, notes, and next-session reminders.",
        status: "Planned",
        icon: TimerReset
      }
    ]
  },
  {
    slug: "real-estate",
    title: "Real Estate",
    description: "Property-oriented calculators and checklists for repeatable decisions.",
    icon: Building2,
    tools: [
      {
        name: "Property Notes",
        summary: "Capture deal notes in a consistent, comparable format.",
        status: "Planned",
        icon: Home
      },
      {
        name: "Listing Scanner",
        summary: "A place for quick screening criteria and follow-up questions.",
        status: "Planned",
        icon: Search
      }
    ]
  }
];

const toolPages = {
  "/tools/music/staff-paper-generator": {
    title: "Printable Music Staff Paper Generator",
    category: "Music",
    description: "Create professional printable music staff paper in PDF format. Customize page size, staff type, spacing, orientation, and layout for composition, arranging, music theory, and classroom use.",
    page: "staffPaper",
    icon: FileText
  },
  "/tools/music/bell-usage-chart-maker": {
    title: "Bell Usage Chart Maker",
    seoTitle: "Bell Usage Chart Maker | LBWD Music Tools",
    seoDescription: "Upload a standard MusicXML file and generate a complete bell usage chart in seconds. The tool scans every pitched note in the score, ignores rests and unpitched percussion, combines duplicate pitches, and counts how many times each bell appears.",
    category: "Music",
    description: "Upload a standard MusicXML file and generate a complete bell usage chart in seconds. The tool scans every pitched note in the score, ignores rests and unpitched percussion, combines duplicate pitches, and counts how many times each bell appears.",
    page: "bellUsageChart",
    icon: Bell
  },
  "/tools/music/scale-generator": {
    title: "Printable Scale Generator",
    category: "Music",
    description: "Create free printable music scales in PDF format for piano students, band, orchestra, choir, and music teachers. Generate major, natural minor, harmonic minor, and melodic minor scales with treble, bass, or grand staff layouts in seconds.",
    page: "scale",
    icon: Music
  },
  "/tools/music/instrument-range-transposition": {
    title: "Instrument Range & Transposition Chart | Free Music Tool",
    category: "Music",
    description: "Compare 43 common band and orchestral instruments by range, transposition, concert pitch, clefs, and playable notes with this free reference tool.",
    page: "instrumentRangeTransposition",
    icon: Search
  },
  "/tools/music/metronome": {
    title: "Metronome",
    category: "Music",
    description: "Set a tempo, tap to find the beat, and practice with a simple browser metronome for steady rhythm.",
    page: "metronome",
    icon: Music2
  }
};

const StaffPaperGeneratorPage = lazy(() => import("./pages/StaffPaperPage"));
const BellUsageChartMakerPage = lazy(() => import("./pages/BellUsageChartMakerPage"));
const ScaleGeneratorPage = lazy(() => import("./pages/ScaleGeneratorPage"));
const InstrumentRangeTranspositionPage = lazy(() => import("./pages/InstrumentRangeTranspositionPage"));
const MetronomePage = lazy(() => import("./pages/MetronomePage"));
const OpenSourceLicensesPage = lazy(() => import("./pages/OpenSourceLicensesPage"));

const toolComponentByPage = {
  staffPaper: StaffPaperGeneratorPage,
  bellUsageChart: BellUsageChartMakerPage,
  scale: ScaleGeneratorPage,
  instrumentRangeTransposition: InstrumentRangeTranspositionPage,
  metronome: MetronomePage
};

const staticPages = {
  "/tools/open-source-licenses": {
    page: "openSourceLicenses",
    title: "Open Source Licenses",
    seoTitle: "Open Source Licenses | LBWD Media",
    seoDescription: "Open-source software licenses and third-party notices for LBWD Media online tools.",
    canonicalUrl: "https://lbwdm.com/tools/open-source-licenses",
    imageUrl: "https://lbwdm.com/images/logo.jpg",
    imageAlt: "LBWD Media LLC logo"
  }
};

const staticPageComponentByPage = {
  openSourceLicenses: OpenSourceLicensesPage
};

const musicLandingSeo = {
  title: "Free Online Music Tools for Musicians | LBWD Media",
  description: "Explore free online music tools for musicians, teachers, students, and arrangers, including a metronome, staff paper generator, scale tools, and more.",
  ogDescription: "Free browser-based music tools for practice, notation, scales, rhythm, and more from LBWD Media.",
  url: "https://lbwdm.com/tools/music",
  imageUrl: "https://lbwdm.com/og/lbwdm_music_tools_og.jpg",
  imageAlt: "Benny with a metronome and sheet music representing LBWD Media's free online music tools."
};

function upsertHeadTag(selector, createTag) {
  const existing = document.head.querySelector(selector);
  if (existing) {
    return existing;
  }

  const element = createTag();
  document.head.appendChild(element);
  return element;
}

function getCurrentRoute() {
  const path = window.location.pathname.replace(/\/+$/, "");
  const activeStaticPage = staticPages[path] || null;

  if (activeStaticPage) {
    return { activeSection: null, activeTool: null, activeStaticPage };
  }

  const activeTool = toolPages[path] || null;

  if (activeTool) {
    const section = sections.find((item) => item.title === activeTool.category) || null;
    return { activeSection: section, activeTool, activeStaticPage: null };
  }

  const slug = path.split("/").filter(Boolean).pop();
  return {
    activeSection: sections.find((section) => section.slug === slug) || null,
    activeTool: null,
    activeStaticPage: null
  };
}

function App() {
  const { activeSection, activeTool, activeStaticPage } = getCurrentRoute();

  React.useEffect(() => {
    let title = "Online Productivity Tools | LBWD Media LLC";

    if (activeStaticPage) {
      title = activeStaticPage.seoTitle;
    } else if (activeTool?.page === "staffPaper") {
      title = "Free Printable Staff Paper Generator | LBWD Media";
    } else if (activeTool?.page === "metronome") {
      title = "Free Online Metronome with Tap Tempo | LBWD Music Tools";
    } else if (activeTool?.page === "bellUsageChart") {
      title = activeTool.seoTitle;
    } else if (activeTool) {
      title = `${activeTool.title} | LBWD Media LLC`;
    } else if (activeSection?.slug === "music") {
      title = musicLandingSeo.title;
    } else if (activeSection) {
      title = `${activeSection.title} Tools | LBWD Media LLC`;
    }

    document.title = title;
  }, [activeSection, activeTool, activeStaticPage]);

  React.useEffect(() => {
    if (window.location.pathname.replace(/\/+$/, "") !== "/tools/music") {
      return;
    }

    const description = musicLandingSeo.description;
    const ogDescription = musicLandingSeo.ogDescription;
    const title = musicLandingSeo.title;

    upsertHeadTag('link[rel="canonical"]', () => {
      const link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
      return link;
    }).setAttribute("href", musicLandingSeo.url);

    upsertHeadTag('meta[name="description"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", description);

    upsertHeadTag('meta[property="og:type"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:type");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "website");

    upsertHeadTag('meta[property="og:title"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:title");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", title);

    upsertHeadTag('meta[property="og:description"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:description");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", ogDescription);

    upsertHeadTag('meta[property="og:url"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:url");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", musicLandingSeo.url);

    upsertHeadTag('meta[property="og:site_name"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:site_name");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "LBWD Media");

    upsertHeadTag('meta[property="og:image"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", musicLandingSeo.imageUrl);

    upsertHeadTag('meta[property="og:image:width"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:width");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "1200");

    upsertHeadTag('meta[property="og:image:height"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:height");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "630");

    upsertHeadTag('meta[property="og:image:type"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:type");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "image/jpeg");

    upsertHeadTag('meta[property="og:image:alt"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:alt");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", musicLandingSeo.imageAlt);

    upsertHeadTag('meta[name="twitter:card"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "twitter:card");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "summary_large_image");

    upsertHeadTag('meta[name="twitter:title"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "twitter:title");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", title);

    upsertHeadTag('meta[name="twitter:description"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "twitter:description");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", ogDescription);

    upsertHeadTag('meta[name="twitter:image"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "twitter:image");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", musicLandingSeo.imageUrl);

    upsertHeadTag('meta[name="twitter:image:alt"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "twitter:image:alt");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", musicLandingSeo.imageAlt);
  }, [activeSection, activeTool, activeStaticPage]);

  React.useEffect(() => {
    if (!activeStaticPage) {
      return;
    }

    applySeoMetadata(activeStaticPage);
  }, [activeStaticPage]);

  const ActiveToolPage = activeTool ? toolComponentByPage[activeTool.page] : null;
  const ActiveStaticPage = activeStaticPage ? staticPageComponentByPage[activeStaticPage.page] : null;

  return (
    <>
      <Header activeSection={activeSection} />
      <main className="tools-shell">
        <Suspense fallback={<ToolPageLoadingState />}>
          {ActiveToolPage ? (
            <ActiveToolPage tool={activeTool} />
          ) : ActiveStaticPage ? (
            <ActiveStaticPage />
          ) : activeSection ? (
            <SectionPage section={activeSection} />
          ) : (
            <LandingPage />
          )}
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function ToolPageLoadingState() {
  return (
    <div className="tool-page-loading" role="status" aria-live="polite" aria-busy="true">
      <span>Loading tool page...</span>
    </div>
  );
}

function applySeoMetadata({ seoTitle, seoDescription, canonicalUrl, imageUrl, imageAlt }) {
  document.title = seoTitle;

  upsertHeadTag('link[rel="canonical"]', () => {
    const link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
    return link;
  }).setAttribute("href", canonicalUrl);

  upsertHeadTag('meta[name="description"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", seoDescription);

  upsertHeadTag('meta[property="og:type"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", "og:type");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", "website");

  upsertHeadTag('meta[property="og:title"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", "og:title");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", seoTitle);

  upsertHeadTag('meta[property="og:description"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", "og:description");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", seoDescription);

  upsertHeadTag('meta[property="og:url"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", "og:url");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", canonicalUrl);

  upsertHeadTag('meta[property="og:site_name"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", "og:site_name");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", "LBWD Media");

  if (imageUrl) {
    upsertHeadTag('meta[property="og:image"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", imageUrl);

    upsertHeadTag('meta[property="og:image:alt"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:alt");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", imageAlt || "");

    upsertHeadTag('meta[property="og:image:width"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:width");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "1200");

    upsertHeadTag('meta[property="og:image:height"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:height");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "630");

    upsertHeadTag('meta[property="og:image:type"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:image:type");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", "image/jpeg");
  }

  upsertHeadTag('meta[name="twitter:card"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "twitter:card");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", imageUrl ? "summary_large_image" : "summary");

  upsertHeadTag('meta[name="twitter:title"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "twitter:title");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", seoTitle);

  upsertHeadTag('meta[name="twitter:description"]', () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "twitter:description");
    document.head.appendChild(meta);
    return meta;
  }).setAttribute("content", seoDescription);

  if (imageUrl) {
    upsertHeadTag('meta[name="twitter:image"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "twitter:image");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", imageUrl);

    upsertHeadTag('meta[name="twitter:image:alt"]', () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "twitter:image:alt");
      document.head.appendChild(meta);
      return meta;
    }).setAttribute("content", imageAlt || "");
  }
}

function Header({ activeSection }) {
  return (
    <header className="site-header">
      <a className="logo" href="/" aria-label="LBWD Media LLC home">
        <img src="/images/logo.jpg" width="386" height="372" alt="" aria-hidden="true" />
        <div>
          <strong>LBWD Media LLC</strong>
          <span>Smart Solutions. Loyal Partners.</span>
        </div>
      </a>

      <nav className="site-nav" aria-label="Primary">
        <a className="nav-link" href="/">Home</a>
        <details className="nav-dropdown">
          <summary className="nav-link nav-summary">Services</summary>
          <div className="nav-dropdown-menu" role="group" aria-label="Services submenu">
            <a className="nav-dropdown-link" href="/#services">Overview</a>
            <a className="nav-dropdown-link" href="/apps">Apps</a>
            <a className="nav-dropdown-link" href="/webconsulting">Web Strategy</a>
          </div>
        </details>
        <a className="nav-link" href="/#about">About</a>
        <a className="nav-link" href="/contact">Contact</a>
        <details className="nav-dropdown is-active">
          <summary className="nav-link nav-summary is-active">Tools</summary>
          <div className="nav-dropdown-menu" role="group" aria-label="Tools submenu">
            <a className={`nav-dropdown-link${!activeSection ? " is-active" : ""}`} href="/tools">
              Overview
            </a>
            {sections.map((section) => (
              <a
                key={section.slug}
                className={`nav-dropdown-link${activeSection?.slug === section.slug ? " is-active" : ""}`}
                href={`/tools/${section.slug}`}
              >
                {section.title}
              </a>
            ))}
          </div>
        </details>
      </nav>
    </header>
  );
}

function LandingPage() {
  return (
    <>
      <section className="tools-hero" aria-labelledby="tools-title">
        <div className="tools-hero-copy">
          <p className="eyebrow">LBWD Media tools</p>
          <h1 id="tools-title">Online Productivity Tools</h1>
          <p>
            A growing collection of lightweight web utilities for common planning,
            creative, and business workflows.
          </p>
          <div className="buttons">
            <a className="btn primary" href="/tools/general">Browse General</a>
            <a className="btn secondary" href="#tool-index">View Index</a>
          </div>
        </div>
        <div className="tools-hero-panel" aria-label="Tools categories">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <a className="category-link" href={`/tools/${section.slug}`} key={section.slug}>
                <span className="category-icon"><Icon size={22} aria-hidden="true" /></span>
                <span>
                  <strong>{section.title}</strong>
                  <small>{section.description}</small>
                </span>
                <ChevronRight size={20} aria-hidden="true" />
              </a>
            );
          })}
        </div>
      </section>

      <section className="tool-index" id="tool-index" aria-labelledby="index-title">
        <div className="section-heading">
          <p className="section-kicker">Tool index</p>
          <h2 id="index-title">Start with a category</h2>
        </div>
        <div className="tool-grid">
          {sections.map((section) => (
            <CategoryCard section={section} key={section.slug} />
          ))}
        </div>
      </section>
    </>
  );
}

function SectionPage({ section }) {
  return (
    <>
      <section className={`section-hero${section.slug === "music" ? " music-hero" : ""}`}>
        <p className="eyebrow">Online Productivity Tools</p>
        <h1>{section.slug === "music" ? "Free Music Tools" : section.title}</h1>
        <p>{section.description}</p>
      </section>

      {section.slug === "music" ? (
        <section className="section-intro" aria-label="Music tools introduction">
          <p>LBWD Music Tools provides free browser-based utilities for music educators, performers, arrangers, church musicians, handbell directors, and students.</p>
          <p>Our tools are designed to save time, simplify repetitive tasks, and help you spend more time making music.</p>
        </section>
      ) : null}

      <section className="tool-index" aria-labelledby={`${section.slug}-tools`}>
        <div className="section-heading">
          <h2 id={`${section.slug}-tools`}>{section.slug === "general" ? "Free Tools" : "Free Music Tools"}</h2>
        </div>
        <div className="tool-list">
          {section.tools.map((tool) => {
            const Icon = tool.icon;
            const ToolShell = tool.href ? "a" : "article";
            const toolProps = tool.href ? { href: tool.href } : {};
            return (
              <ToolShell className={`tool-row${tool.featured ? " tool-row--featured" : ""}`} key={tool.name} {...toolProps}>
                <span className="tool-icon"><Icon size={22} aria-hidden="true" /></span>
                <div>
                  {tool.featured ? <span className="tool-row-badge">Featured</span> : null}
                  <h3>{tool.name}</h3>
                  <p>{tool.summary}</p>
                </div>
                <span className="tool-status">{tool.status}</span>
              </ToolShell>
            );
          })}
        </div>
      </section>

      {section.slug === "music" ? (
        <section className="section-roadmap" aria-label="More tools are on the way">
          <div className="section-roadmap-inner">
            <h2>More tools are on the way</h2>
            <p>We&apos;re building a growing collection of free music tools for teachers, students, church musicians, handbell directors, arrangers, and performers.</p>
            <p>Upcoming tools include practice tracking, music theory helpers, handbell utilities, rehearsal planning, and more.</p>
          </div>
        </section>
      ) : null}
    </>
  );
}

function CategoryCard({ section }) {
  const Icon = section.icon;

  return (
    <article className="tool-card">
      <span className="tool-card-icon"><Icon size={26} aria-hidden="true" /></span>
      <h3>{section.title}</h3>
      <p>{section.description}</p>
      <a href={`/tools/${section.slug}`}>
        Open {section.title}
        <ChevronRight size={18} aria-hidden="true" />
      </a>
    </article>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-logo">
        <img src="/images/logo.jpg" width="386" height="372" alt="LBWD Media LLC logo" />
        <strong>LBWD Media LLC</strong>
        <p className="footer-tagline">Free browser-based music tools for teachers, students, directors, arrangers, and performers.</p>
      </div>

      <div>
        <p className="footer-heading">Explore</p>
        <a href="/">Home</a>
        <a href="/tools">Tools</a>
        <a href="/apps">Apps</a>
        <a href="/#services">Services</a>
        <a href="/#about">About</a>
        <a href="/contact">Contact</a>
        <a href="/privacy">Privacy Policy</a>
      </div>

      <div>
        <p className="footer-heading">Tools</p>
        {sections.map((section) => (
          <a key={section.slug} href={`/tools/${section.slug}`}>{section.title}</a>
        ))}
      </div>

      <div>
        <p className="footer-heading">Let's Connect</p>
        <p><a href="mailto:info@lbwdm.com">info@lbwdm.com</a></p>
        <p>
          <button
            type="button"
            className="footer-cookie-settings"
            onClick={() => window.CookieConsent?.showPreferences()}
          >
            Cookie Preferences
          </button>
        </p>
        <div className="footer-socials">
          <a href="mailto:info@lbwdm.com" aria-label="Email LBWD Media LLC">
            <i className="fa-regular fa-envelope" aria-hidden="true"></i>
          </a>
        </div>
      </div>

      <div>
        <p className="copyright">
          © {currentYear} LBWD Media LLC{" "}
          <span className="copyright-separator" aria-hidden="true">·</span>{" "}
          <a href="/privacy">Privacy</a>{" "}
          <span className="copyright-separator" aria-hidden="true">·</span>{" "}
          <a href="/terms">Terms</a>{" "}
          <span className="copyright-separator" aria-hidden="true">·</span>{" "}
          <a href="/tools/open-source-licenses">Open Source Licenses</a>
        </p>
      </div>
    </footer>
  );
}

createRoot(document.getElementById("root")).render(<App />);
